import { signingFieldSizeForField, type SignatureField } from '@corner/shared';
import { asFiniteNumber, normalizeFieldRectToPercent } from './coerceNumeric';

/**
 * Token field payloads are scoped to this invitee. Normalize signer/party,
 * 1-based page indices, label/type (from `role` when stripped), and sane
 * geometry so overlays always render — mirrors backend `normalizeSignerFieldsForTokenResponse`.
 */
export function recipientViewFields(
  fields: SignatureField[] | undefined | null,
  pageCount?: number,
): SignatureField[] {
  if (!fields?.length) return [];
  const pc = pageCount != null && pageCount >= 1 ? pageCount : 9999;

  const mapped = fields.map((f) => {
    let page = Math.max(1, Math.floor(asFiniteNumber(f.page) ?? 1));
    if (page > pc) page = pc;

    let width = asFiniteNumber(f.width);
    let height = asFiniteNumber(f.height);
    let x = asFiniteNumber(f.x);
    let y = asFiniteNumber(f.y);

    if (width != null && height != null && width > 0 && height > 0) {
      const n = normalizeFieldRectToPercent(x ?? undefined, y ?? undefined, width, height);
      width = n.width;
      height = n.height;
      x = n.x ?? x;
      y = n.y ?? y;
    }

    const roleLower = (f.role ?? '').toLowerCase();
    const roleSaysDate = roleLower.endsWith('_date');
    const roleSaysInitials = roleLower.endsWith('_initials');
    const roleSaysTitle = roleLower.endsWith('_title');
    const roleSaysName = roleLower.endsWith('_name');
    const roleSaysSig = roleLower.endsWith('_signature');
    const inferredFromRole: SignatureField['type'] | undefined = roleSaysDate
      ? 'date'
      : roleSaysInitials
        ? 'initials'
        : roleSaysTitle
          ? 'text'
          : roleSaysName
            ? 'text'
            : roleSaysSig
              ? 'signature'
              : undefined;

    let type: SignatureField['type'] = f.type ?? 'signature';
    if (!f.type) {
      type = inferredFromRole ?? 'signature';
    } else if (f.type === 'signature' && inferredFromRole && inferredFromRole !== 'signature') {
      type = inferredFromRole;
    }

    let label = (f.label ?? '').trim();
    if (!label) {
      if (roleSaysDate) label = 'Date';
      else if (roleSaysInitials) label = 'Initials';
      else if (roleSaysTitle) label = 'Title';
      else if (roleSaysName) label = 'Name';
      else if (roleSaysSig) label = 'Signature';
      else label = 'Signature';
    }

    if (width == null || !Number.isFinite(width) || width <= 0) {
      const size = signingFieldSizeForField({ type, role: f.role, label });
      width = size.width;
      height = height != null && Number.isFinite(height) && height > 0 ? height : size.height;
    }
    if (height == null || !Number.isFinite(height) || height <= 0) {
      height = signingFieldSizeForField({ type, role: f.role, label }).height;
    }
    if (x == null || !Number.isFinite(x)) x = 10;
    if (y == null || !Number.isFinite(y)) y = 80;

    return {
      ...f,
      page,
      type,
      label,
      x,
      y,
      width,
      height,
      signer: 'you' as const,
      party: 'self' as const,
    };
  });


  return mapped;
}
